import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import * as api from '../lib/api';
import type { Adventure, TripGroup } from '../types';
import { AdventureDetail } from '../components/AdventureDetail';
import { AddAdventureModal } from '../components/AddAdventureModal';
import { SettingsPanel } from '../components/SettingsPanel';
import { WorldMap } from '../components/WorldMap';
import { useMetadata, useHTMLExists, queryKeys } from '../lib/queries';

/** Given sorted adventures and trip_groups, produce an ordered list of
 *  sections: { group?: TripGroup, adventures: Adventure[] }
 *  Adventures not in any group appear as ungrouped sections. */
function buildGroupedSections(
  adventures: Adventure[],
  tripGroups: TripGroup[]
): { group?: TripGroup; adventures: Adventure[] }[] {
  if (!tripGroups.length) {
    return [{ adventures }];
  }

  type ResolvedGroup = { group: TripGroup; advIds: Set<string> };
  const resolved: ResolvedGroup[] = [];

  for (const g of tripGroups) {
    const ids = new Set(g.adventure_ids ?? []);
    if (ids.size === 0) continue;
    resolved.push({ group: g, advIds: ids });
  }

  // Sort groups by name, descending -- "2027 Trip" before "2026 Trip" before
  // "2025 Trip". Previously sorted by whichever group happened to contain
  // the single highest-orderId adventure, which tracks individual manual
  // reordering (drag-and-drop) rather than the year in the group's own
  // name, producing an order like 2026/2027/2025 with no visible logic to
  // it. Name-based descending is what every reasonable reading of "sorted
  // descending" here actually means, and matches the same convention
  // LifeDeck's own trip-group tabs already use.
  resolved.sort((a, b) => (b.group.name || '').localeCompare(a.group.name || ''));

  const claimed = new Set<string>();
  resolved.forEach(r => r.advIds.forEach(id => claimed.add(id)));

  // Build sections by walking `resolved` itself (now in the sort order that
  // matters) rather than by walking `adventures` and pushing a section the
  // first time one of its members is encountered -- that walk order is
  // orderId-descending, which is exactly the ordering this was supposed to
  // stop depending on, so it silently undid the sort above regardless of
  // what `resolved` was sorted by. Adventures within each group still come
  // out orderId-descending, since `adventures` is already sorted that way
  // and filter() preserves relative order.
  const sections: { group?: TripGroup; adventures: Adventure[] }[] = resolved.map(r => ({
    group: r.group,
    adventures: adventures.filter(a => r.advIds.has(a.id)),
  }));

  const ungrouped = adventures.filter(a => !claimed.has(a.id));
  if (ungrouped.length > 0) {
    sections.push({ adventures: ungrouped });
  }

  return sections;
}

export function DashboardPage() {
  const { user, signOut, updateUser } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useMetadata(user ?? null);
  const adventures: Adventure[] = data?.adventures ?? [];
  const tripGroups: TripGroup[] = data?.tripGroups ?? [];
  const subdomain = data?.subdomain ?? '';

  // The cached identity's s3_prefix (localStorage, set once at login) goes
  // stale the moment it changes server-side -- e.g. setSubdomain -- since
  // nothing else ever refetches it. Every upload/display URL keys off
  // user.s3_prefix, so a stale value means every upload gets rejected by
  // the backend's "Key does not match user prefix" check. getMetadata now
  // returns the current value on every load; self-heal the cache here
  // instead of needing a full sign-out/sign-in to pick it up.
  useEffect(() => {
    if (data?.s3Prefix && user && data.s3Prefix !== user.s3_prefix) {
      updateUser({ s3_prefix: data.s3Prefix });
    }
  }, [data?.s3Prefix, user, updateUser]);

  const { data: htmlExists = false, refetch: refetchHtmlExists } = useHTMLExists(user?.s3_prefix);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishingCount, setPublishingCount] = useState(0);
  const [publishingTotal, setPublishingTotal] = useState(0);
  const publishTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [viewMode, setViewMode] = useState<'published' | 'planning'>('published');

  // Derive selected adventure from live query data so it always reflects latest edits
  const selected = adventures.find(a => a.id === selectedId) ?? null;

  const invalidateMetadata = () => {
    if (user) queryClient.invalidateQueries({ queryKey: queryKeys.metadata(user.user_id) });
  };

  const handleGenerate = async () => {
    if (!user) return;
    setGenerating(true);
    setPublishingCount(adventures.length);
    setPublishingTotal(adventures.length);
    publishTimerRef.current = setInterval(() => {
      setPublishingCount(prev => Math.max(1, prev - 5));
    }, 1000);
    await api.generateHTML();
    if (publishTimerRef.current) { clearInterval(publishTimerRef.current); publishTimerRef.current = null; }
    setPublishingCount(0);
    await new Promise(r => setTimeout(r, 2000));
    refetchHtmlExists();
    setGenerating(false);
  };

  const handleAdventureAdded = () => {
    setShowAdd(false);
    invalidateMetadata();
  };

  const handleAdventureUpdated = () => {
    invalidateMetadata();
    refetchHtmlExists();
  };

  // ── Drag-to-reorder adventures (pointer-based, not HTML5 DnD) ──
  const [advDragIdx, setAdvDragIdx] = useState<number | null>(null);
  const [advDragOverIdx, setAdvDragOverIdx] = useState<number | null>(null);

  const advEntriesRef = useRef<Adventure[]>([]);

  const advDragRef = useRef<{
    idx: number;
    offsetY: number;
    clone: HTMLElement;
    container: HTMLElement;
    card: HTMLElement;
    currentOver: number;
    moved: boolean;
  } | null>(null);

  const startAdvDrag = (e: React.PointerEvent, index: number) => {
    if (e.button !== 0) return;
    const card = (e.currentTarget as HTMLElement).closest('[data-adv-item]') as HTMLElement;
    if (!card) return;
    const container = card.closest('[data-adv-list]') as HTMLElement;
    if (!container) return;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const cardRect = card.getBoundingClientRect();

    const clone = card.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
      position: fixed;
      left: ${cardRect.left}px;
      top: ${cardRect.top}px;
      width: ${cardRect.width}px;
      z-index: 9999;
      opacity: 0.9;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      pointer-events: none;
      transition: none;
      border-radius: 8px;
    `;
    document.body.appendChild(clone);
    card.style.opacity = '0.2';

    advDragRef.current = {
      idx: index,
      offsetY: e.clientY - cardRect.top,
      clone,
      container,
      card,
      currentOver: index,
      moved: false,
    };
    setAdvDragIdx(index);
    e.preventDefault();
  };

  const moveAdvDrag = (e: React.PointerEvent) => {
    const d = advDragRef.current;
    if (!d) return;
    e.preventDefault();
    d.moved = true;
    d.clone.style.top = `${e.clientY - d.offsetY}px`;

    const cards = Array.from(d.container.querySelectorAll('[data-adv-item]'));
    const centerY = e.clientY;
    let overIdx = d.currentOver;
    for (let ci = 0; ci < cards.length; ci++) {
      const r = cards[ci].getBoundingClientRect();
      const midY = r.top + r.height / 2;
      if (centerY < midY) { overIdx = ci; break; }
      if (ci === cards.length - 1) overIdx = ci;
    }
    if (overIdx !== d.currentOver) {
      d.currentOver = overIdx;
      setAdvDragOverIdx(overIdx);
    }
  };

  const endAdvDrag = async (e: React.PointerEvent) => {
    const d = advDragRef.current;
    if (!d) return;
    if (d.clone.parentElement) d.clone.parentElement.removeChild(d.clone);
    d.card.style.opacity = '1';

    const fromIdx = d.idx;
    const toIdx = d.currentOver;
    advDragRef.current = null;
    setAdvDragIdx(null);
    setAdvDragOverIdx(null);

    if (d.moved && fromIdx !== toIdx) {
      const reordered = [...advEntriesRef.current];
      const [item] = reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, item);

      // Recalculate orderIds: top = 10000, second = 9999, etc.
      const orderMap: Record<string, number> = {};
      reordered.forEach((adv, i) => { orderMap[adv.id] = 10000 - i; });

      await api.reorderAdventures(orderMap);
      invalidateMetadata();
    }
  };

  // Determine which trip groups are planning vs published
  const publishedGroups = useMemo(
    () => tripGroups.filter(g => g.published !== false),
    [tripGroups]
  );
  const planningGroups = useMemo(
    () => tripGroups.filter(g => g.published === false),
    [tripGroups]
  );

  // Determine which adventure IDs belong to planning groups
  const planningAdvIds = useMemo(() => {
    const ids = new Set<string>();
    for (const g of planningGroups) {
      for (const aid of (g.adventure_ids || [])) ids.add(aid);
    }
    return ids;
  }, [planningGroups]);

  const planningCount = planningAdvIds.size;

  // Filter adventures based on view mode
  const filteredAdventures = useMemo(() => {
    if (viewMode === 'planning') return adventures.filter(a => planningAdvIds.has(a.id));
    // Published: everything NOT in a planning group
    return adventures.filter(a => !planningAdvIds.has(a.id));
  }, [adventures, planningAdvIds, viewMode]);
  advEntriesRef.current = filteredAdventures;

  const activeGroups = viewMode === 'planning' ? planningGroups : publishedGroups;
  const sections = useMemo(
    () => buildGroupedSections(filteredAdventures, activeGroups),
    [filteredAdventures, activeGroups]
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Top bar */}
      <header className="bg-white border-b border-sand-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-adventure/10 flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-adventure">
                <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
              </svg>
            </div>
            <h1 className="font-display text-xl text-sand-900 hidden sm:block">Adventure Editor</h1>
          </div>

          <div className="flex items-center gap-2">
            {htmlExists && (
              <a href={subdomain ? `https://${subdomain}.wanderbooks.net` : api.indexURL(user.s3_prefix)} target="_blank" rel="noreferrer"
                className="btn-secondary text-sm py-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>
                <span className="hidden sm:inline">View Site</span>
              </a>
            )}

            <button onClick={handleGenerate} disabled={generating}
              className="btn-secondary text-sm py-2">
              {generating ? (
                <span className="inline-block w-4 h-4 border-2 border-sand-400/30 border-t-sand-600 rounded-full animate-spin" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
              )}
              <span className="hidden sm:inline">{generating ? 'Generating...' : 'Publish'}</span>
            </button>

            <button onClick={() => setShowSettings(true)} className="btn-secondary text-sm py-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            </button>

            <button onClick={signOut} className="text-sand-400 hover:text-sand-600 transition-colors p-2" title="Sign out">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left column: Adventure list */}
          <div className={`w-full lg:w-80 lg:flex-shrink-0 ${selected ? 'hidden lg:block' : ''}`}>
            <div className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:flex lg:flex-col">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h2 className="font-display text-2xl text-sand-900">Adventures</h2>
                <button onClick={() => setShowAdd(true)} className="btn-primary text-sm py-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  New
                </button>
              </div>

              {/* Published / Planning tabs */}
              {planningGroups.length > 0 && (
                <div className="flex gap-1 bg-sand-100 rounded-lg p-1 mb-4 flex-shrink-0">
                  <button
                    onClick={() => { setViewMode('published'); setSelectedId(null); }}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all
                      ${viewMode === 'published' ? 'bg-white text-sand-900 shadow-sm' : 'text-sand-500 hover:text-sand-700'}`}>
                    Published
                  </button>
                  <button
                    onClick={() => { setViewMode('planning'); setSelectedId(null); }}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all flex items-center justify-center gap-1.5
                      ${viewMode === 'planning' ? 'bg-white text-sand-900 shadow-sm' : 'text-sand-500 hover:text-sand-700'}`}>
                    Planning
                    {planningCount > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${viewMode === 'planning' ? 'bg-adventure/10 text-adventure' : 'bg-sand-200 text-sand-500'}`}>
                        {planningCount}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <span className="inline-block w-8 h-8 border-2 border-sand-300 border-t-adventure rounded-full animate-spin" />
                </div>
              ) : filteredAdventures.length === 0 ? (
                <div className="card p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-sand-100 flex items-center justify-center mx-auto mb-4">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sand-400"><path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/></svg>
                  </div>
                  {viewMode === 'planning' ? (
                    <>
                      <p className="text-sand-500 font-medium">No planning trips yet</p>
                      <p className="text-sand-400 text-sm mt-1">Create a trip group in Settings → Trips and mark it as Planning</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sand-500 font-medium">No adventures yet</p>
                      <p className="text-sand-400 text-sm mt-1">Create your first adventure to get started</p>
                    </>
                  )}
                </div>
              ) : (
                <div data-adv-list className="lg:overflow-y-auto lg:flex-1 lg:min-h-0 lg:pr-1 space-y-4">
                    {/* An ungrouped trailing section only gets a header when trip
                        groups are actually in use (activeGroups.length > 0) --
                        otherwise every adventure is ungrouped by definition (no
                        trip groups configured at all) and a lone "Unassigned"
                        label would be misleading rather than informative. When
                        groups ARE in use, this previously rendered with no
                        header, no map, and no indent at all, making ungrouped
                        adventures look like a silent continuation of whichever
                        group happened to sort last. */}
                    {sections.map((section, sIdx) => {
                      const showHeader = !!section.group || activeGroups.length > 0;
                      return (
                    <div key={sIdx}>
                      {showHeader && (
                        <>
                          <div className="flex items-center gap-2 mb-2 mt-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-adventure flex-shrink-0">
                              <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
                            </svg>
                            <span className="text-xs font-semibold text-adventure uppercase tracking-wide truncate">
                              {section.group?.name ?? 'Unassigned'}
                            </span>
                            {section.group && viewMode === 'planning' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 flex-shrink-0">
                                Draft
                              </span>
                            )}
                          </div>
                          <WorldMap
                            adventures={section.adventures}
                            title={section.group?.name ?? 'Unassigned'}
                            onSelectAdventure={a => setSelectedId(a.id)}
                          />
                        </>
                      )}

                      <div className="space-y-1">
                        {section.adventures.map(adv => {
                          const flatIdx = filteredAdventures.findIndex(a => a.id === adv.id);
                          return (
                          <div
                            key={adv.id}
                            data-adv-item
                            className={`flex items-center rounded-lg transition-all duration-150 ${
                              advDragIdx === flatIdx ? 'opacity-20' : ''
                            } ${advDragOverIdx === flatIdx && advDragIdx !== null && advDragIdx !== flatIdx
                              ? advDragIdx < flatIdx
                                ? 'border-b-2 border-b-blue-400'
                                : 'border-t-2 border-t-blue-400'
                              : ''
                            } ${selectedId === adv.id
                              ? 'bg-adventure/10 border border-adventure/20'
                              : 'hover:bg-sand-100'
                            } ${showHeader ? 'ml-2' : ''}`}
                          >
                            <div
                              onPointerDown={e => startAdvDrag(e, flatIdx)}
                              onPointerMove={moveAdvDrag}
                              onPointerUp={endAdvDrag}
                              onPointerCancel={endAdvDrag}
                              className="flex items-center justify-center px-1.5 py-3 cursor-grab active:cursor-grabbing
                                text-sand-200 hover:text-sand-400 select-none touch-none transition-colors flex-shrink-0"
                              title="Drag to reorder"
                            >
                              <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
                                <circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/>
                                <circle cx="3" cy="7" r="1.2"/><circle cx="7" cy="7" r="1.2"/>
                                <circle cx="3" cy="12" r="1.2"/><circle cx="7" cy="12" r="1.2"/>
                              </svg>
                            </div>
                            <button
                              onClick={() => setSelectedId(adv.id)}
                              className={`flex-1 text-left px-3 py-3 min-w-0
                                ${selectedId === adv.id ? 'text-adventure-dark' : 'text-sand-800'}`}
                            >
                              <span className="font-medium block truncate">{adv.description}</span>
                              <span className="text-xs text-sand-400 mt-0.5 block">
                                {adv.entries.length} day{adv.entries.length !== 1 ? 's' : ''}
                                {adv.entries.reduce((n, e) => n + e.photos.length, 0) > 0 &&
                                  ` · ${adv.entries.reduce((n, e) => n + e.photos.length, 0)} photos`}
                              </span>
                            </button>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>

          {/* Right column: Detail panel */}
          <div className={`flex-1 min-w-0 ${selected ? '' : 'hidden lg:block'}`}>
            {selected ? (
              <div>
                <button onClick={() => setSelectedId(null)}
                  className="lg:hidden flex items-center gap-1.5 text-adventure text-sm font-medium mb-4 -ml-1 hover:underline">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  All Adventures
                </button>
                <AdventureDetail
                  adventure={selected}
                  user={user}
                  onUpdate={handleAdventureUpdated}
                  onDelete={() => { setSelectedId(null); invalidateMetadata(); }}
                />
              </div>
            ) : (
              <div className="card p-12 text-center text-sand-400">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="mx-auto mb-4 text-sand-300">
                  <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
                  <path d="M9 3v14"/><path d="M15 7v14"/>
                </svg>
                <p className="font-medium text-sand-500">Select an adventure to edit</p>
                <p className="text-sm mt-1">Or create a new one to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAdd && <AddAdventureModal user={user} onClose={() => setShowAdd(false)} onAdded={handleAdventureAdded} />}
      {showSettings && (
        <SettingsPanel
          user={user}
          onClose={() => setShowSettings(false)}
          adventures={adventures}
          tripGroups={tripGroups}
          onTripGroupsChange={invalidateMetadata}
        />
      )}

      {/* Matches AdventureDetail's photo-upload indicator on purpose --
          same solid brand background + real progress bar treatment,
          requested directly after that one shipped. publishingCount is a
          cosmetic countdown (real publish is usually too fast for its
          1-second tick to ever fire once), not a live per-adventure
          count, but the bar still gives a truthful sense of "started" vs
          "about done" off of it. */}
      {generating && (
        <div className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 bg-adventure text-white rounded-xl px-5 py-4 shadow-2xl z-50">
          <div className="flex items-center gap-3 mb-2.5">
            <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" />
            <span className="text-base font-semibold">
              Publishing {publishingTotal} adventure{publishingTotal !== 1 ? 's' : ''}...
            </span>
          </div>
          <div className="h-2 bg-white/25 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${publishingTotal ? ((publishingTotal - publishingCount) / publishingTotal) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
